import { CreateSavePointSource } from "@/utils/savePoint";

export interface RestorationDecision {
  shouldRestore: boolean;
  targetStep: number;
  toastMessage: string;
}

/**
 * Determines restoration behavior based on save point source and conditions
 */
export function determineRestorationBehavior(
  shouldRestore: boolean,
  source: CreateSavePointSource,
  age: number,
  savedStep: number | undefined,
  hasImages: boolean
): RestorationDecision {
  let shouldRestoreState = false;
  let targetStep = 1;
  let toastMessage = "Your design has been restored.";

  if (shouldRestore) {
    // Explicit restoration request via URL parameter
    shouldRestoreState = true;

    if (source === "credit_purchase_redirect") {
      // For credit redirects, check if images exist
      // If images exist (front images for step 3, or any images for modification), stay on current step
      // If no images, revert to the step BEFORE generation attempt
      const step = savedStep || 1;
      if (hasImages) {
        // User has images (e.g., on step 3 with front images, or modifying existing images)
        // Keep current step so they can continue from where they left off
        targetStep = step;
      } else {
        // User is generating for first time, go back to previous step
        // Step 2 -> 1, Step 4 -> 3, Step 3 -> 3 (no change)
        targetStep = step === 2 ? 1 : step === 4 ? 3 : step;
      }
      toastMessage = hasImages
        ? "Your design has been restored. Keep editing or create a new preview."
        : "Your design has been restored. You can create a preview when you’re ready.";
    } else if (source === "auth_redirect") {
      // Always restore to step 1 for auth redirects - we want the form, not the generation step
      targetStep = 1;
      toastMessage = "Welcome back. Your design has been restored.";
    } else {
      targetStep = savedStep || 1;
    }
  } else if (
    source === "credit_purchase_redirect" ||
    source === "auth_redirect"
  ) {
    // Always restore from redirects, even without URL param
    shouldRestoreState = true;

    if (source === "credit_purchase_redirect") {
      // For credit redirects, check if images exist
      // If images exist (front images for step 3, or any images for modification), stay on current step
      // If no images, revert to the step BEFORE generation attempt
      const step = savedStep || 1;
      if (hasImages) {
        // User has images (e.g., on step 3 with front images, or modifying existing images)
        // Keep current step so they can continue from where they left off
        targetStep = step;
      } else {
        // User is generating for first time, go back to previous step
        // Step 2 -> 1, Step 4 -> 3, Step 3 -> 3 (no change)
        targetStep = step === 2 ? 1 : step === 4 ? 3 : step;
      }
      toastMessage = hasImages
        ? "Your design has been restored. Keep editing or create a new preview."
        : "Your design has been restored. You can create a preview when you’re ready.";
    } else {
      // For auth redirects, always restore to step 1 - we want the form, not the generation step
      targetStep = 1;
      toastMessage = "Welcome back. Your design has been restored.";
    }
  } else if (
    (source === "auto_save" || source === "before_unload") &&
    age < 30000
  ) {
    // Restore recent auto-saves (< 30 seconds = likely a refresh)
    shouldRestoreState = true;
    targetStep = savedStep || 1;
    toastMessage = "Your design has been restored.";
  }

  return {
    shouldRestore: shouldRestoreState,
    targetStep,
    toastMessage,
  };
}
